import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Switch,
  ActivityIndicator,
  Alert,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import {
  OpportunityType,
  GradeLevel,
  OpportunityStatus,
  InteractionType,
} from "../types";
import {
  createOrganizationOpportunity,
  updateOrganizationOpportunity,
} from "../services/parseService";

interface OrganizationOpportunityFormProps {
  opportunityId?: string | null; // null for new opportunity
  opportunityData?: any; // existing data for editing
  onClose: () => void;
  onSuccess: () => void;
  organizationUserId: string;
}

const OrganizationOpportunityForm: React.FC<OrganizationOpportunityFormProps> = ({
  opportunityId,
  opportunityData,
  onClose,
  onSuccess,
  organizationUserId,
}) => {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  
  // Form state
  const [title, setTitle] = useState(opportunityData?.title || "");
  const [description, setDescription] = useState(opportunityData?.description || "");
  const [types, setTypes] = useState<OpportunityType[]>(
    opportunityData?.type ? (Array.isArray(opportunityData.type) ? opportunityData.type : [opportunityData.type]) : []
  );
  // Get organization name from current user
  const getOrganizationName = () => {
    if (!currentUser) return "";
    
    if (currentUser.get) {
      return currentUser.get('organizationName') || currentUser.get('username') || "";
    }
    
    return currentUser.organizationName || currentUser.username || "";
  };

  const [organization, setOrganization] = useState(
    opportunityData?.organization || getOrganizationName()
  );
  const [location, setLocation] = useState(opportunityData?.location || "");
  const [website, setWebsite] = useState(opportunityData?.website || "");
  const [eligibility, setEligibility] = useState(opportunityData?.eligibility || "");
  const [cost, setCost] = useState(opportunityData?.cost || "");
  const [isYearlyRecurring, setIsYearlyRecurring] = useState(opportunityData?.isYearlyRecurring || false);
  const [tags, setTags] = useState<string[]>(opportunityData?.tags || []);
  const [tagInput, setTagInput] = useState("");
  const [gradeLevels, setGradeLevels] = useState<GradeLevel[]>(opportunityData?.gradeLevel || []);
  const [interactionTypes, setInteractionTypes] = useState<InteractionType[]>(
    opportunityData?.interactionTypes || []
  );

  // Date states with string representation for display
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [startDateString, setStartDateString] = useState("");
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [endDateString, setEndDateString] = useState("");
  const [applicationOpenDate, setApplicationOpenDate] = useState<Date>(new Date());
  const [applicationOpenDateString, setApplicationOpenDateString] = useState("");
  const [applicationCloseDate, setApplicationCloseDate] = useState<Date>(new Date());
  const [applicationCloseDateString, setApplicationCloseDateString] = useState("");

  // Loading states
  const [isSaving, setIsSaving] = useState(false);

  // Options
  const opportunityTypes: OpportunityType[] = [
    "Competition",
    "Grant",
    "Scholarship",
    "Hackathon",
    "Internship",
    "Workshop/Programs",
    "Conference",
    "Other",
  ];

  const gradeOptions: GradeLevel[] = [
    "Elementary",
    "Middle School",
    "High School",
    "Undergraduate",
    "Graduate",
    "Professional",
    "All Levels",
  ];

  const interactionOptions: InteractionType[] = [
    "In-Person",
    "Virtual",
    "Hybrid",
  ];

  // Initialize dates from existing data
  React.useEffect(() => {
    if (opportunityData) {
      const parseDate = (dateObj: any): { date: Date; formatted: string } => {
        if (!dateObj) return { date: new Date(), formatted: "" };

        let date: Date;
        if (dateObj instanceof Date) {
          date = dateObj;
        } else if (dateObj.iso) {
          date = new Date(dateObj.iso);
        } else if (typeof dateObj === "string") {
          date = new Date(dateObj);
        } else {
          return { date: new Date(), formatted: "" };
        }

        if (isNaN(date.getTime())) return { date: new Date(), formatted: "" };

        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear();

        return {
          date,
          formatted: `${month}/${day}/${year}`,
        };
      };

      if (opportunityData.startDate) {
        const parsed = parseDate(opportunityData.startDate);
        setStartDate(parsed.date);
        setStartDateString(parsed.formatted);
      }
      if (opportunityData.endDate) {
        const parsed = parseDate(opportunityData.endDate);
        setEndDate(parsed.date);
        setEndDateString(parsed.formatted);
      }
      if (opportunityData.applicationOpenDate) {
        const parsed = parseDate(opportunityData.applicationOpenDate);
        setApplicationOpenDate(parsed.date);
        setApplicationOpenDateString(parsed.formatted);
      }
      if (opportunityData.applicationCloseDate) {
        const parsed = parseDate(opportunityData.applicationCloseDate);
        setApplicationCloseDate(parsed.date);
        setApplicationCloseDateString(parsed.formatted);
      }
    }
  }, [opportunityData]);

  const handleSave = async () => {
    // Basic validation
    if (!title.trim()) {
      const message = "Please enter a title for the opportunity.";
      if (Platform.OS === 'web') {
        alert(message);
      } else {
        Alert.alert("Missing Information", message);
      }
      return;
    }

    if (!organization.trim()) {
      const message = "Please enter the organization name.";
      if (Platform.OS === 'web') {
        alert(message);
      } else {
        Alert.alert("Missing Information", message);
      }
      return;
    }

    if (!location.trim()) {
      const message = "Please enter a location.";
      if (Platform.OS === 'web') {
        alert(message);
      } else {
        Alert.alert("Missing Information", message);
      }
      return;
    }

    if (types.length === 0) {
      const message = "Please select at least one opportunity type.";
      if (Platform.OS === 'web') {
        alert(message);
      } else {
        Alert.alert("Missing Information", message);
      }
      return;
    }

    if (!website.trim()) {
      const message = "Please enter a website URL.";
      if (Platform.OS === 'web') {
        alert(message);
      } else {
        Alert.alert("Missing Information", message);
      }
      return;
    }

    try {
      setIsSaving(true);

      // Prepare opportunity data
      const opportunityData: any = {
        title: title.trim(),
        description: description.trim(),
        type: types.length === 1 ? types[0] : types.join(", "),
        organization: organization.trim(),
        location: location.trim(),
        eligibility: eligibility.trim(),
        website: website.trim(),
        tags,
        gradeLevel: gradeLevels,
        interactionTypes: interactionTypes.length > 0 ? interactionTypes : undefined,
        isYearlyRecurring,
        isClaimed: true, // Organization-created opportunities are automatically claimed
        cost: cost.trim(),
        status: calculateStatus() as OpportunityStatus,
        createdBy: organizationUserId,
      };

      // Handle dates
      if (startDateString.trim()) opportunityData.startDate = startDate;
      if (endDateString.trim()) opportunityData.endDate = endDate;
      if (applicationOpenDateString.trim()) opportunityData.applicationOpenDate = applicationOpenDate;
      if (applicationCloseDateString.trim()) opportunityData.applicationCloseDate = applicationCloseDate;

      // Create or update opportunity
      if (opportunityId) {
        await updateOrganizationOpportunity(opportunityId, opportunityData, organizationUserId);
      } else {
        await createOrganizationOpportunity(opportunityData);
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error("Error saving opportunity:", err);
      
      const errorMessage = err instanceof Error ? err.message : String(err);
      const fullErrorMessage = `Failed to save opportunity: ${errorMessage}`;
      
      if (Platform.OS === 'web') {
        alert(fullErrorMessage);
      } else {
        Alert.alert("Error", fullErrorMessage);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const calculateStatus = (): OpportunityStatus => {
    const now = new Date();

    // Case 1: Applications are open (highest priority)
    if (applicationOpenDateString && applicationCloseDateString) {
      if (now >= applicationOpenDate && now <= applicationCloseDate) {
        return "Open";
      }
    }

    // Case 2: Coming Soon - if we have start date and it's in the future
    if (startDateString && now < startDate) {
      return "Coming Soon";
    }

    // Case 3: No date information at all - default to "Open"
    if (!startDateString && !endDateString && !applicationOpenDateString && !applicationCloseDateString) {
      return "Open";
    }

    // Case 4: Default fallback
    return "Open";
  };

  // Parse a date string in MM/DD/YYYY format
  const parseCustomDateString = (dateString: string): Date | null => {
    if (!dateString.trim()) return null;

    const dateParts = dateString.split("/");
    if (dateParts.length !== 3) return null;

    const month = parseInt(dateParts[0], 10) - 1; // Months are 0-indexed
    const day = parseInt(dateParts[1], 10);
    const year = parseInt(dateParts[2], 10);

    if (isNaN(month) || isNaN(day) || isNaN(year)) return null;

    const date = new Date(year, month, day);
    return date;
  };

  // Update both the Date object and the string value
  const handleDateInput = (
    dateString: string,
    dateType: "start" | "end" | "appOpen" | "appClose",
  ) => {
    const date = parseCustomDateString(dateString);

    switch (dateType) {
      case "start":
        setStartDateString(dateString);
        if (date) setStartDate(date);
        break;
      case "end":
        setEndDateString(dateString);
        if (date) setEndDate(date);
        break;
      case "appOpen":
        setApplicationOpenDateString(dateString);
        if (date) setApplicationOpenDate(date);
        break;
      case "appClose":
        setApplicationCloseDateString(dateString);
        if (date) setApplicationCloseDate(date);
        break;
    }
  };

  const toggleGradeLevel = (level: GradeLevel) => {
    if (gradeLevels.includes(level)) {
      setGradeLevels(gradeLevels.filter((gl) => gl !== level));
    } else {
      setGradeLevels([...gradeLevels, level]);
    }
  };

  const toggleType = (optionType: OpportunityType) => {
    if (types.includes(optionType)) {
      setTypes(types.filter((t) => t !== optionType));
    } else {
      setTypes([...types, optionType]);
    }
  };

  const toggleInteractionType = (type: InteractionType) => {
    if (interactionTypes.includes(type)) {
      setInteractionTypes(interactionTypes.filter((t) => t !== type));
    } else {
      setInteractionTypes([...interactionTypes, type]);
    }
  };

  const addTag = () => {
    if (tagInput.trim() !== "" && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  return (
    <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={onClose}>
      <TouchableOpacity style={[styles.modal, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF" }]} activeOpacity={1} onPress={(e) => e.stopPropagation()}>
        {/* Header */}
        <View style={[styles.header, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", borderBottomColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
          <View style={styles.headerLeft}>
            <TouchableOpacity style={[styles.closeButton, { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]} onPress={onClose}>
              <Ionicons name="close" size={20} color={theme === "dark" ? "#C2C2C2" : "#6B7280"} />
            </TouchableOpacity>
            <View style={styles.headerTitleContainer}>
              <Text style={[styles.headerTitle, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}>
                {opportunityId ? "Edit Opportunity" : "Create Opportunity"}
              </Text>
              {/* <Text style={[styles.headerSubtitle, { color: theme === "dark" ? "#C2C2C2" : "#6B7280" }]}>
                {opportunityId ? "Update opportunity details" : "Fill in the details below"}
              </Text> */}
            </View>
          </View>
        </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Basic Information Section */}
        <View style={[styles.formSection, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
          <Text style={[styles.sectionTitle, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}>Basic Information</Text>

          <Text style={[styles.label, { color: theme === "dark" ? "#FAFAFA" : "#374151" }]}>Title*</Text>
          <TextInput
            style={[styles.textInput, { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB", color: theme === "dark" ? "#FAFAFA" : "#111827" }]}
            value={title}
            onChangeText={setTitle}
            placeholder="e.g., Summer Science Camp 2025"
            placeholderTextColor={theme === "dark" ? "#9CA3AF" : "#9CA3AF"}
          />

          <Text style={[styles.label, { color: theme === "dark" ? "#FAFAFA" : "#374151" }]}>Organization*</Text>
          <TextInput
            style={[styles.textInput, styles.readOnlyInput, { 
              backgroundColor: theme === "dark" ? "#2A2A2E" : "#F3F4F6", 
              borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB", 
              color: theme === "dark" ? "#9CA3AF" : "#6B7280" 
            }]}
            value={organization}
            editable={false}
            placeholder="Organization name (auto-filled)"
            placeholderTextColor={theme === "dark" ? "#9CA3AF" : "#9CA3AF"}
          />

          <Text style={[styles.label, { color: theme === "dark" ? "#FAFAFA" : "#374151" }]}>Type* (Select all that apply)</Text>
          <View style={styles.optionsContainer}>
            {opportunityTypes.map((optionType) => (
              <TouchableOpacity
                key={optionType}
                style={[
                  styles.optionButton,
                  { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" },
                  types.includes(optionType) && styles.optionButtonSelected,
                ]}
                onPress={() => toggleType(optionType)}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.optionText,
                    { color: theme === "dark" ? "#C2C2C2" : "#6B7280" },
                    types.includes(optionType) && styles.optionTextSelected,
                  ]}
                >
                  {optionType}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={[styles.label, { color: theme === "dark" ? "#FAFAFA" : "#374151" }]}>Location*</Text>
          <TextInput
            style={[styles.textInput, { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB", color: theme === "dark" ? "#FAFAFA" : "#111827" }]}
            value={location}
            onChangeText={setLocation}
            placeholder="e.g., Vancouver, BC or Online"
            placeholderTextColor={theme === "dark" ? "#9CA3AF" : "#9CA3AF"}
          />

          <Text style={[styles.label, { color: theme === "dark" ? "#FAFAFA" : "#374151" }]}>Website*</Text>
          <TextInput
            style={[styles.textInput, { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB", color: theme === "dark" ? "#FAFAFA" : "#111827" }]}
            value={website}
            onChangeText={setWebsite}
            placeholder="https://example.com/opportunity"
            placeholderTextColor={theme === "dark" ? "#9CA3AF" : "#9CA3AF"}
            keyboardType="url"
            autoCapitalize="none"
          />

          <Text style={[styles.label, { color: theme === "dark" ? "#FAFAFA" : "#374151" }]}>Description*</Text>
          <TextInput
            style={[styles.textAreaInput, { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB", color: theme === "dark" ? "#FAFAFA" : "#111827" }]}
            value={description}
            onChangeText={setDescription}
            placeholder="Describe what participants will do, learn, or achieve. Include key details about the program structure, activities, and benefits."
            placeholderTextColor={theme === "dark" ? "#9CA3AF" : "#9CA3AF"}
            multiline
            numberOfLines={6}
            textAlignVertical="top"
          />
        </View>

        {/* Dates Section */}
        <View style={[styles.formSection, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
          <Text style={[styles.sectionTitle, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}>Dates</Text>

          <Text style={[styles.label, { color: theme === "dark" ? "#FAFAFA" : "#374151" }]}>Program/Event Dates (MM/DD/YYYY)</Text>
          <Text style={[styles.helpText, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>When does the actual program or event take place?</Text>
          <View style={styles.dateRow}>
            <View style={[styles.dateInputContainer, { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
              <Ionicons name="calendar-outline" size={20} color={theme === "dark" ? "#C2C2C2" : "#6B7280"} />
              <TextInput
                style={[styles.dateInput, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}
                value={startDateString}
                onChangeText={(text) => handleDateInput(text, "start")}
                placeholder="MM/DD/YYYY"
                placeholderTextColor={theme === "dark" ? "#9CA3AF" : "#9CA3AF"}
                keyboardType="numbers-and-punctuation"
              />
            </View>

            <View style={[styles.dateInputContainer, { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
              <Ionicons name="calendar-outline" size={20} color={theme === "dark" ? "#C2C2C2" : "#6B7280"} />
              <TextInput
                style={[styles.dateInput, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}
                value={endDateString}
                onChangeText={(text) => handleDateInput(text, "end")}
                placeholder="MM/DD/YYYY"
                placeholderTextColor={theme === "dark" ? "#9CA3AF" : "#9CA3AF"}
                keyboardType="numbers-and-punctuation"
              />
            </View>
          </View>

          <Text style={[styles.label, { color: theme === "dark" ? "#FAFAFA" : "#374151" }]}>Application Period (MM/DD/YYYY)</Text>
          <Text style={[styles.helpText, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>When can people apply? Leave blank if applications are always open.</Text>
          <View style={styles.dateRow}>
            <View style={[styles.dateInputContainer, { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
              <Ionicons name="calendar-outline" size={20} color={theme === "dark" ? "#C2C2C2" : "#6B7280"} />
              <TextInput
                style={[styles.dateInput, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}
                value={applicationOpenDateString}
                onChangeText={(text) => handleDateInput(text, "appOpen")}
                placeholder="MM/DD/YYYY"
                placeholderTextColor={theme === "dark" ? "#9CA3AF" : "#9CA3AF"}
                keyboardType="numbers-and-punctuation"
              />
            </View>

            <View style={[styles.dateInputContainer, { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
              <Ionicons name="calendar-outline" size={20} color={theme === "dark" ? "#C2C2C2" : "#6B7280"} />
              <TextInput
                style={[styles.dateInput, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}
                value={applicationCloseDateString}
                onChangeText={(text) => handleDateInput(text, "appClose")}
                placeholder="MM/DD/YYYY"
                placeholderTextColor={theme === "dark" ? "#9CA3AF" : "#9CA3AF"}
                keyboardType="numbers-and-punctuation"
              />
            </View>
          </View>

          <View style={styles.switchRow}>
            <Text style={[styles.switchLabel, { color: theme === "dark" ? "#FAFAFA" : "#374151" }]}>Yearly Recurring Event</Text>
            <Switch
              value={isYearlyRecurring}
              onValueChange={setIsYearlyRecurring}
              trackColor={{ false: theme === "dark" ? "#2A2A2E" : "#E5E7EB", true: "#DBEAFE" }}
              thumbColor={isYearlyRecurring ? "#2563EB" : "#FFFFFF"}
            />
          </View>
        </View>

        {/* Eligibility & Details Section */}
        <View style={[styles.formSection, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
          <Text style={[styles.sectionTitle, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}>Eligibility & Details</Text>

          <Text style={[styles.label, { color: theme === "dark" ? "#FAFAFA" : "#374151" }]}>Grade Levels</Text>
          <View style={styles.optionsContainer}>
            {gradeOptions.map((level) => (
              <TouchableOpacity
                key={level}
                style={[
                  styles.optionButton,
                  { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" },
                  gradeLevels.includes(level) && styles.optionButtonSelected,
                ]}
                onPress={() => toggleGradeLevel(level)}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.optionText,
                    { color: theme === "dark" ? "#C2C2C2" : "#6B7280" },
                    gradeLevels.includes(level) && styles.optionTextSelected,
                  ]}
                >
                  {level}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={[styles.label, { color: theme === "dark" ? "#FAFAFA" : "#374151" }]}>Interaction Type</Text>
          <View style={styles.optionsContainer}>
            {interactionOptions.map((type) => (
              <TouchableOpacity
                key={type}
                style={[
                  styles.optionButton,
                  { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" },
                  interactionTypes.includes(type) && styles.optionButtonSelected,
                ]}
                onPress={() => toggleInteractionType(type)}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.optionText,
                    { color: theme === "dark" ? "#C2C2C2" : "#6B7280" },
                    interactionTypes.includes(type) && styles.optionTextSelected,
                  ]}
                >
                  {type}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={[styles.label, { color: theme === "dark" ? "#FAFAFA" : "#374151" }]}>Eligibility Requirements</Text>
          <TextInput
            style={[styles.textAreaInput, { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB", color: theme === "dark" ? "#FAFAFA" : "#111827" }]}
            value={eligibility}
            onChangeText={setEligibility}
            placeholder="e.g., Students in grades 9-12, BC residents, or specific prerequisites"
            placeholderTextColor={theme === "dark" ? "#9CA3AF" : "#9CA3AF"}
            multiline
            numberOfLines={4}
            textAlignVertical="top"
          />

          <Text style={[styles.label, { color: theme === "dark" ? "#FAFAFA" : "#374151" }]}>Cost</Text>
          <TextInput
            style={[styles.textInput, { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB", color: theme === "dark" ? "#FAFAFA" : "#111827" }]}
            value={cost}
            onChangeText={setCost}
            placeholder="e.g., Free, $50, $100-200, Scholarships available"
            placeholderTextColor={theme === "dark" ? "#9CA3AF" : "#9CA3AF"}
          />

          <Text style={[styles.label, { color: theme === "dark" ? "#FAFAFA" : "#374151" }]}>Tags</Text>
          <Text style={[styles.helpText, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>Add keywords to help people find your opportunity (e.g., science, coding, leadership)</Text>
          <View style={styles.tagInputContainer}>
            <TextInput
              style={[styles.tagInput, { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB", color: theme === "dark" ? "#FAFAFA" : "#111827" }]}
              value={tagInput}
              onChangeText={setTagInput}
              placeholder="Type a tag and press Enter"
              placeholderTextColor={theme === "dark" ? "#9CA3AF" : "#9CA3AF"}
              onSubmitEditing={addTag}
            />
            <TouchableOpacity style={styles.addTagButton} onPress={addTag}>
              <Ionicons name="add" size={20} color="#FFFFFF" />
            </TouchableOpacity>
          </View>

          <View style={styles.tagsContainer}>
            {tags.map((tag) => (
              <View key={tag} style={[styles.tag, { backgroundColor: theme === "dark" ? "#34343B" : "#EEF2FF" }]}>
                <Text style={[styles.tagText, { color: theme === "dark" ? "#60A5FA" : "#2563EB" }]}>{tag}</Text>
                <TouchableOpacity onPress={() => removeTag(tag)}>
                  <Ionicons name="close-circle" size={16} color={theme === "dark" ? "#C2C2C2" : "#6B7280"} />
                </TouchableOpacity>
              </View>
            ))}
          </View>
        </View>
      </ScrollView>
      
      {/* Footer with buttons */}
      <View style={[styles.footer, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", borderTopColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
        <View style={styles.footerActions}>
          <TouchableOpacity
            style={[styles.cancelButton, { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}
            onPress={onClose}
            activeOpacity={0.7}
          >
            <Text style={[styles.cancelButtonText, { color: theme === "dark" ? "#C2C2C2" : "#6B7280" }]}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.saveButton, isSaving && styles.savingButton]}
            onPress={handleSave}
            disabled={isSaving}
            activeOpacity={0.7}
          >
            {isSaving ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <>
                <Ionicons name="checkmark" size={16} color="#FFFFFF" />
                <Text style={styles.saveButtonText}>
                  {opportunityId ? "Update" : "Create"}
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>
      </TouchableOpacity>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    width: '95%',
    maxWidth: 1000,
    maxHeight: '95%',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.25,
    shadowRadius: 20,
    elevation: 10,
  },
  
  // Header
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  closeButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
    borderWidth: 1,
  },
  headerTitleContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  headerSubtitle: {
    fontSize: 14,
    fontFamily: "Inter-Regular",
    marginTop: 2,
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  cancelButton: {
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    marginRight: 12,
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  saveButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#2563EB",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  savingButton: {
    backgroundColor: "#9CA3AF",
  },
  saveButtonText: {
    fontSize: 14,
    color: "#FFFFFF",
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
    marginLeft: 4,
  },

  // Footer
  footer: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
  },
  footerActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
  },

  // Content
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 20,
  },
  formSection: {
    marginBottom: 24,
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 20,
    fontFamily: "Inter-SemiBold",
  },
  label: {
    fontSize: 14,
    fontWeight: "500",
    marginBottom: 8,
    fontFamily: "Inter-Medium",
  },
  helpText: {
    fontSize: 12,
    marginBottom: 8,
    fontFamily: "Inter-Regular",
    fontStyle: "italic",
  },
  textInput: {
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    marginBottom: 16,
    fontFamily: "Inter-Regular",
  },
  readOnlyInput: {
    opacity: 0.8,
  },
  textAreaInput: {
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    marginBottom: 16,
    minHeight: 100,
    fontFamily: "Inter-Regular",
  },

  // Options
  optionsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 16,
    marginHorizontal: -4,
  },
  optionButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    marginHorizontal: 4,
    marginBottom: 8,
  },
  optionButtonSelected: {
    backgroundColor: "#2563EB",
    borderColor: "#2563EB",
  },
  optionText: {
    fontSize: 14,
    fontFamily: "Inter-Medium",
  },
  optionTextSelected: {
    color: "#FFFFFF",
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Dates
  dateRow: {
    flexDirection: "row",
    marginBottom: 16,
    marginHorizontal: -6,
  },
  dateInputContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginHorizontal: 6,
  },
  dateInput: {
    flex: 1,
    fontSize: 16,
    marginLeft: 8,
    fontFamily: "Inter-Regular",
  },

  // Switch
  switchRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginVertical: 8,
  },
  switchLabel: {
    fontSize: 16,
    fontFamily: "Inter-Regular",
  },

  // Tags
  tagInputContainer: {
    flexDirection: "row",
    marginBottom: 12,
  },
  tagInput: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    marginRight: 8,
    fontFamily: "Inter-Regular",
  },
  addTagButton: {
    backgroundColor: "#2563EB",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  tagsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 16,
    marginHorizontal: -2,
  },
  tag: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginHorizontal: 2,
    marginBottom: 4,
  },
  tagText: {
    fontSize: 12,
    marginRight: 4,
    fontFamily: "Inter-Medium",
  },
});

export default OrganizationOpportunityForm;
